/**
 * Hitscan geometry: aim direction, and ray-versus-player-capsule.
 *
 * Players are tested analytically rather than through Rapier, for the same reason
 * `world.ts` uses Rapier for queries only: the collision world is static, while
 * player positions are *rewound* per shot (`realtime/src/sim/rewind.ts`). Inserting
 * or moving colliders per shot per player would be expensive, stateful, and would
 * make hit resolution depend on solver internals. A closed-form ray-capsule test is
 * cheap, exact, and identical everywhere it runs.
 *
 * Level geometry still goes through Rapier — see `CollisionWorld.castRay` — because
 * brushes are exactly what its broad-phase is for.
 */
import { addScaled, dot, sub, type Vec3, vec3 } from './math.js';
import { MOVEMENT } from './movement.js';

/**
 * Unit aim vector for a yaw/pitch pair.
 *
 * The convention must match `wishDirection` in movement.ts or a player would shoot
 * somewhere other than they walk: right-handed, Y-up, yaw 0 faces -Z, and positive
 * pitch looks **up**. Getting the Z signs wrong is invisible in isolation — shots
 * just mirror front-to-back — so this is pinned by a test.
 */
export function aimDirection(yaw: number, pitch: number): Vec3 {
  const cosPitch = Math.cos(pitch);
  return vec3(-Math.sin(yaw) * cosPitch, Math.sin(pitch), -Math.cos(yaw) * cosPitch);
}

/**
 * First intersection of a ray with a **vertical** capsule, as a distance along the
 * ray, or null if it misses.
 *
 * Vertical because players never tilt: the capsule is a segment from
 * `center.y - halfHeight` to `center.y + halfHeight` at (center.x, center.z),
 * inflated by `radius`. `halfHeight` excludes the end caps, matching Rapier's
 * convention and `CAPSULE_HALF_HEIGHT` — the same convention that silently makes
 * players the wrong height when confused.
 *
 * Solved as an infinite cylinder clipped to the segment, plus a sphere at whichever
 * cap the ray passes beyond. A ray starting *inside* the capsule returns 0, which is
 * the correct answer for a point-blank shot.
 */
export function rayCapsule(
  origin: Vec3,
  direction: Vec3,
  center: Vec3,
  halfHeight: number,
  radius: number,
): number | null {
  const top = center.y + halfHeight;
  const bottom = center.y - halfHeight;

  // Inside the capsule already? Then the shot connects at zero distance.
  if (pointInsideCapsule(origin, center, halfHeight, radius)) return 0;

  let best: number | null = null;

  // ─── Cylindrical body: solve in the XZ plane, then clip by height ──────────
  const dx = origin.x - center.x;
  const dz = origin.z - center.z;
  const a = direction.x * direction.x + direction.z * direction.z;

  if (a > 1e-9) {
    const b = 2 * (dx * direction.x + dz * direction.z);
    const c = dx * dx + dz * dz - radius * radius;
    const discriminant = b * b - 4 * a * c;

    if (discriminant >= 0) {
      const root = Math.sqrt(discriminant);
      for (const t of [(-b - root) / (2 * a), (-b + root) / (2 * a)]) {
        if (t < 0) continue;
        const y = origin.y + direction.y * t;
        if (y >= bottom && y <= top) best = min(best, t);
      }
    }
  }

  // ─── End caps: a sphere at each ────────────────────────────────────────────
  // Both are tested rather than only the near one: a ray angled steeply through the
  // body can exit past the far cap, and picking a cap by the ray's Y sign gets that
  // case wrong.
  for (const capY of [top, bottom]) {
    const t = raySphere(origin, direction, vec3(center.x, capY, center.z), radius);
    if (t !== null) best = min(best, t);
  }

  return best;
}

/** Nearest non-negative intersection of a ray with a sphere, or null. */
export function raySphere(origin: Vec3, direction: Vec3, center: Vec3, radius: number): number | null {
  const toCenter = sub(origin, center);
  const a = dot(direction, direction);
  if (a <= 1e-9) return null;

  const b = 2 * dot(toCenter, direction);
  const c = dot(toCenter, toCenter) - radius * radius;
  const discriminant = b * b - 4 * a * c;
  if (discriminant < 0) return null;

  const root = Math.sqrt(discriminant);
  const near = (-b - root) / (2 * a);
  if (near >= 0) return near;
  const far = (-b + root) / (2 * a);
  return far >= 0 ? far : null;
}

export function pointInsideCapsule(
  point: Vec3,
  center: Vec3,
  halfHeight: number,
  radius: number,
): boolean {
  const clampedY = Math.min(center.y + halfHeight, Math.max(center.y - halfHeight, point.y));
  const dx = point.x - center.x;
  const dy = point.y - clampedY;
  const dz = point.z - center.z;
  return dx * dx + dy * dy + dz * dz <= radius * radius;
}

/** Point at `distance` along a ray — where an impact effect belongs. */
export function rayPoint(origin: Vec3, direction: Vec3, distance: number): Vec3 {
  return addScaled(origin, direction, distance);
}

// ─── Hit zones ───────────────────────────────────────────────────────────────

export const HIT_ZONE = {
  head: 0,
  torso: 1,
  limb: 2,
} as const;

export type HitZone = (typeof HIT_ZONE)[keyof typeof HIT_ZONE];

/**
 * Zone boundaries as heights above the player's feet, in metres.
 *
 * Horizontal bands rather than per-limb volumes, and that is a deliberate limit. A band
 * test is a single comparison on the impact point, runs identically on client and server,
 * and — the part that actually matters — is *explainable*: a player who watches the killcam
 * can see the shot landed above the shoulders. Per-bone hitboxes would need the rewind
 * buffer to store a pose rather than a position, roughly quadrupling it, to resolve
 * disputes nobody can perceive at these ranges.
 *
 * Cost: shooting a raised arm registers as a torso hit. Accepted, because the alternative
 * is a headshot for hitting a hand.
 *
 * `MOVEMENT.height` is 1.8m, so: head is the top 0.32m, legs the bottom 0.85m.
 */
export const HIT_ZONE_HEIGHTS = {
  /**
   * At or above this height above the feet, it is a head hit.
   *
   * 1.48, from the rig rather than from round numbers (2026-09-08): the Synty survivor's Neck
   * joint sits at 1.449m and the Head joint at 1.569m with the mesh topping out at 1.788m, so
   * the drawn head occupies roughly 1.47–1.79m. The old 1.55 put the jaw and the lower third
   * of the head in the torso band — a shot that visibly hit the face and paid torso damage,
   * which reads as "hit registration is broken". The band is the head as drawn.
   */
  headBottom: 1.48,
  /** Below this height above the feet, it is a limb (leg) hit. */
  torsoBottom: 0.85,
} as const;

/**
 * Which zone an impact landed in.
 *
 * `impactY` and `centerY` are world heights; `centerY` is the entity position, which is the
 * **capsule centre**, not the feet — the same convention `rayCapsule` takes and the reason
 * `MUZZLE_OFFSET_Y` subtracts half the height. Mixing the two puts every hit one band low
 * and turns headshots into torso hits, which reads as "hit registration is broken".
 *
 * `standingHeight` is passed rather than read from MOVEMENT so a crouched or otherwise
 * shortened capsule classifies against its own height instead of a standing one.
 */
/**
 * The capsule a shot is resolved against, given where the movement capsule is and whether the
 * target is crouched.
 *
 * The movement capsule is always `MOVEMENT.height` tall and its centre is what the wire
 * carries, so a crouched target is *not* simply a shorter capsule at the same centre — it is
 * a shorter capsule sitting on the same feet. Getting that wrong moves the whole target up by
 * a quarter of a metre, which is the difference between a head shot and a miss.
 *
 * Shared so the server's resolution, the client's preview and the renderer all size the same
 * body. A client drawing one shape while the server tests another is the class of bug that
 * gets reported as "hit registration is broken".
 */
export function hitCapsule(
  movementCentreY: number,
  crouching: boolean,
): { centreY: number; height: number; halfHeight: number } {
  const height = crouching ? MOVEMENT.crouchHeight : MOVEMENT.height;
  const feetY = movementCentreY - MOVEMENT.height / 2;
  return {
    centreY: feetY + height / 2,
    height,
    // Rapier's convention: the cylindrical section only, excluding the caps.
    halfHeight: Math.max(0.01, height / 2 - MOVEMENT.radius),
  };
}

export function classifyHitZone(impactY: number, centerY: number, standingHeight: number): HitZone {
  const feetY = centerY - standingHeight / 2;
  const heightAboveFeet = impactY - feetY;
  // Scaled by the actual height, so the bands stay proportional if the capsule shrinks.
  const scale = standingHeight / 1.8;

  if (heightAboveFeet >= HIT_ZONE_HEIGHTS.headBottom * scale) return HIT_ZONE.head;
  if (heightAboveFeet < HIT_ZONE_HEIGHTS.torsoBottom * scale) return HIT_ZONE.limb;
  return HIT_ZONE.torso;
}

/** Human-readable zone name, for the HUD, the killfeed and test failures. */
export function hitZoneName(zone: HitZone): string {
  return zone === HIT_ZONE.head ? 'head' : zone === HIT_ZONE.torso ? 'torso' : 'limb';
}

function min(current: number | null, candidate: number): number {
  return current === null ? candidate : Math.min(current, candidate);
}
